import { todayIn, useZone, utcToZoned, zonedToUtc } from './timezone'

/**
 * A date and a time, the way Django admin asks for them.
 *
 * Two boxes, not one: a date with a calendar and a "Today" shortcut, a time
 * with a "Now". The single datetime-local box that was here before was six
 * fields typed left to right with no way to say "today" without spelling the
 * date out.
 *
 * Value in and out is a UTC ISO string; the boxes show and take the zone
 * picked top-right, so what is typed is what the schedule means.
 */
// The calendar and clock buttons are drawn by the browser in black, which on
// this background is a button nobody can see. Inverted, as every other date
// box in Team Tools already does it, and given a pointer so it reads as
// something to click.
const PICKER_BUTTON =
    '[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer ' +
    '[&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100'

const INPUT =
    'rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/50 ' +
    `disabled:opacity-40 ${PICKER_BUTTON}`

/* "Now", and nothing else.
 *
 * There were six of these -- midnight, 6 a.m., noon, 6 p.m., 8 p.m. -- on the
 * theory that they were the hours people schedule at. They were not: a race or
 * a server goes live when somebody decided it should, and a row of guesses at
 * that is six buttons to read past on the way to the time box, on every date
 * field on the page. "Now" stays because it is the one that is never a guess. */

export const DateTimeField = ({
    value,
    onChange,
    disabled,
    label,
}: {
    /** UTC ISO, or empty. */
    value: string | null | undefined
    onChange: (utcIso: string | null) => void
    disabled?: boolean
    label?: string
}) => {
    const [zone] = useZone()
    const { date, time } = utcToZoned(value, zone)

    const set = (nextDate: string, nextTime: string) => onChange(zonedToUtc(nextDate, nextTime || '00:00', zone))

    return (
        <span className='flex flex-row flex-wrap items-center gap-1.5'>
            {label && <span className='text-[10px] uppercase tracking-widest text-white/50'>{label}</span>}
            <input
                type='date'
                value={date}
                onChange={(e) => set(e.target.value, time)}
                disabled={disabled}
                className={INPUT}
            />
            <button
                type='button'
                onClick={() => set(todayIn(zone).date, time || todayIn(zone).time)}
                disabled={disabled}
                className='text-[10px] uppercase tracking-widest text-violet-300 underline disabled:opacity-40'
            >
                today
            </button>
            <input
                type='time'
                value={time}
                onChange={(e) => set(date || todayIn(zone).date, e.target.value)}
                disabled={disabled || !date}
                className={INPUT}
            />
            <button
                type='button'
                disabled={disabled}
                onClick={() => set(todayIn(zone).date, todayIn(zone).time)}
                className='text-[10px] uppercase tracking-widest text-violet-300 underline disabled:opacity-40'
            >
                now
            </button>
        </span>
    )
}

/** The zone, top-right. Every date box on the page follows it. */
export const ZonePicker = () => {
    const [zone, setZone] = useZone()
    return (
        <label className='flex flex-row items-center gap-2 text-[10px] uppercase tracking-widest text-white/50'>
            times in
            <select
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                className='rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs normal-case tracking-normal text-white outline-none focus:border-white/50'
            >
                {ZONES_FOR_PICKER(zone).map((z) => (
                    <option key={z.id} value={z.id} className='bg-neutral-900'>
                        {z.label}
                    </option>
                ))}
            </select>
        </label>
    )
}

import { ZONES } from './timezone'
/** The list, with whatever zone is currently set included even if it is
 *  not one of ours (an old value, or one set by hand). */
const ZONES_FOR_PICKER = (current: string) =>
    ZONES.some((z) => z.id === current) ? ZONES : [...ZONES, { id: current, label: current }]
